import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Location } from '@angular/common';
import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, Subject } from 'rxjs';
import { map, shareReplay, takeUntil, filter } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { OrderParkingService } from '../../services/orderparking.service';
import { SnackbarService } from '../../services/snackbar.service';
import { StateService } from '../../services/state.service';
import { AdminService } from '../../services/admin.service';
import { DebugModalComponent } from '../debugmodal/debugmodal.component';
import { LoginDialogComponent } from '../login-dialog/login-dialog.component';
import { MySettingsComponent } from '../my-settings/my-settings.component';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { EventQueueService, AppEventType } from '../../services/eventqueue.service';
import { MyConfigOrganization } from '../../models/myConfig';
import { BackOfficeService } from '../../services/backoffice.service';
import { MyConfigService } from '../../services/my-config.service';
import { WelcomeComponent } from '../../../app/parkifly-bookings/components/welcome/welcome.component';
import { LoginComponent } from '../../pages/login/login.component';

@Component({
  selector: 'navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NavigationComponent implements OnInit {
  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map(result => result.matches),
    shareReplay()
  );
  currentRoute: string;
  orderNr?: string = null;
  orderNrForm: UntypedFormGroup
  organizations: MyConfigOrganization[] = [];
  myConfigOrganization: MyConfigOrganization;
  componentDestroyed$: Subject<boolean> = new Subject();
  selectedId: any;
  showNavButtons: boolean = false;
  organizationId: string;
  userModifiedInput: boolean = false;

  public showFlygets = false;
  public showNavigation = true;
  public showSearch = true;
  public showParkifly = false;
  public showMenu = true;

  private mobileHideRoutes: string[] = [
    'booking-overview',
    'update-booking',
    'edit-parking',
    'edit-parking-additional-services',
    'cancel-additional-services'
  ];

  constructor(
    private breakpointObserver: BreakpointObserver,
    public authService: AuthService,
    public dialog: MatDialog,
    public snackbarService: SnackbarService,
    public stateService: StateService,
    public adminService: AdminService,
    public orderParkingService: OrderParkingService,
    public router: Router,
    public translate: TranslocoService,
    public location: Location,
    public title: Title,
    public fb: UntypedFormBuilder,
    public eventQueue: EventQueueService,
    public backOfficeService: BackOfficeService,
    public myConfigService: MyConfigService,
    private cdr: ChangeDetectorRef
  ) {
    // router.events.subscribe(event => event instanceof NavigationStart && this.handleRouteChange());
  }

  ngOnInit() {
    this.setOrderNrForm();
    this.checkCurrentRoute();
    this.updateNavButtons();
    this.updateNavigationVisibility();

    this.orderNrForm?.get('searchSelect')?.valueChanges.subscribe(() => {
      this.userModifiedInput = true;
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.componentDestroyed$)
    ).subscribe(() => {
      this.updateNavButtons();
      this.updateNavigationVisibility();
    });

    this.myConfigService.config$.subscribe(config => {
      if (config) {
        if (localStorage.getItem('navigation_organization') != null) {
          this.selectedId = localStorage.getItem('navigation_organization');
        }
        this.getOrganizations();
      }
    });

    this.eventQueue.on(AppEventType.LoginSuccess).subscribe(event => {
      this.showSearch = true;
      this.getOrganizations();
    });
  }

  getBackOfficeModuleName(subModule: any): string {
    if (!subModule) return '';
    if (subModule.menu && subModule.menu.name) {
      let nameVal = subModule.menu.name;
      let parsed: any;
      if (typeof nameVal === 'string') {
        try {
          parsed = JSON.parse(nameVal);
        } catch (e) {
          return nameVal;
        }
      } else {
        parsed = nameVal;
      }

      const currentLang = this.translate.getActiveLang();
      const localizedName = parsed[currentLang] || parsed['en'] || parsed['sv'];
      if (localizedName) {
        return localizedName;
      }
    }
    return subModule.name || '';
  }

  private updateNavigationVisibility() {
    try {
      const urlTree = this.router.parseUrl(this.router.url || '');
      const params = urlTree.queryParams || {};
      const mobile = params['mobile'] === 'true' || params['mobile'] === true;
      const language = params['lang'];
      const path = this.location.path().split('?')[0] || this.router.url.split('?')[0] || '';

      if (language === 'en' || language === 'sv') {
        this.translate.setActiveLang(language);
      }

      const routesWithOwnNavbar = [
        'customer-portal',
        'parkifly-home',
        'my-orders'
      ];

      if (window.self !== window.top) {
        this.showNavigation = false;
      } else if (routesWithOwnNavbar.some(r => path.includes(r))) {
        this.showNavigation = false;
      } else if (mobile && this.mobileHideRoutes.some(r => path.includes(r))) {
        this.showNavigation = false;
      } else {
        this.showNavigation = true;
      }
    } catch (e) {
      this.showNavigation = true;
    }
  }

  checkCurrentRoute() {
    if (
      // window.location.hostname === 'localhost' ||
      window.location.hostname === 'shuttlebuss-web-test.wittysand-9ea05a62.westeurope.azurecontainerapps.io' ||
      window.location.hostname === 'flygets.se' ||
      window.location.hostname === 'www.flygets.se' ||
      window.location.hostname === 'fyrkantig.se' ||
      window.location.hostname === 'www.fyrkantig.se'
    ) {
      this.showFlygets = true;
      this.title.setTitle('Flygets Långtidsparkering');
    }
    else {
      this.showParkifly = true;
      this.title.setTitle('Parkifly');
    }
  }

  openDialog(): void {
    this.dialog.open(LoginDialogComponent, {
      maxHeight: '95vh',
      maxWidth: '95vw'
    });
  }

  openSettings() {
    this.dialog.open(MySettingsComponent, {
      height: '560px',
      width: '500px'
    });
  }

  logout() {
    this.stateService.logout().subscribe(() => {
      this.router.navigate(['/login']);
      this.snackbarService.openSnackBar('Du är utloggad', '');
      this.showNavButtons = false;
      this.showSearch = false;
      this.showMenu = false;
    });
  }

  openDebug(): void {
    const dialogRef = this.dialog.open(DebugModalComponent, {
      position: { right: '5px', left: '5px', top: '65px', bottom: '65px' },
      hasBackdrop: true
    });
    const sub = dialogRef.componentInstance.onAdd.subscribe((data: string) => { });
    dialogRef.afterClosed().subscribe(result => {
      sub.unsubscribe();
    });
  }

  setOrderNrForm() {
    this.orderNrForm = this.fb.group({
      orderNr: [this.orderNr],
      searchSelect: ['O']
    });
    this.orderNrForm?.get('orderNr')?.valueChanges.subscribe(value => {
      this.autoDetectSearchType(value);
    });
  }

  autoDetectSearchType(value: string) {
    if (!value || value.length < 3 || this.userModifiedInput) return;

    const firstThree = value.substring(0, 3);
    const isAllLetters = /^[a-zA-Z]{3}$/.test(firstThree);
    const isAllNumbers = /^[0-9]{3}$/.test(firstThree);

    if (isAllLetters) {
      this.orderNrForm.patchValue({ searchSelect: 'C' }, { emitEvent: false });
      this.cdr.markForCheck();
    } else if (isAllNumbers) {
      this.orderNrForm.patchValue({ searchSelect: 'O' }, { emitEvent: false });
      this.cdr.markForCheck();
    }
  }

  getOrderNr() {
    this.orderNr = this.orderNrForm.controls['orderNr'].value;

    if (this.orderNrForm.controls['searchSelect'].value == 'O') {
      this.backOfficeService
        .getOrderNr(this.orderNr)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(order => {
          this.goToOrder(order.orderId);
        });
    }

    if (this.orderNrForm.controls['searchSelect'].value == 'T') {
      this.backOfficeService
        .getTransactionNr(this.orderNr)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(transaction => {
          this.goToOrder(transaction.orderId);
        });
    }

    if (this.orderNrForm.controls['searchSelect'].value == 'C') {
      this.orderParkingService
        .getOrderParkingCarId(this.orderNr)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(orderParkingVM => {
          this.goToOrder(orderParkingVM.orderId);
        });
    }
  }

  goToOrder(orderId: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${orderId}`]));
    window.open(url, '_blank');
  }

  handleLoginSuccessEvent(isLoggedIn: boolean) {
    this.getOrganizations();
  }

  getOrganizations() {
    this.organizations = this.myConfigService?.myConfig?.organizations;
    if (this.organizations && this.organizations.length > 0) {
      this.showMenu = true;
      if (this.selectedId == null) {
        this.changeOrganization(this.organizations[0].organizationId);
      }
      else {
        if (this.myConfigService.getMyConfigOrganizationById(this.selectedId) != null) {
          this.changeOrganization(this.selectedId);
        }
        else {
          this.changeOrganization(this.organizations[0].organizationId);
        }
      }
    }
  }

  changeOrganization(organizationId: string) {
    this.getOrganizationConfig(organizationId);
    this.setOrganization(organizationId);
    this.organizationId = organizationId
  }

  public setOrganization(organizationId: string) {
    localStorage.setItem('navigation_organization', organizationId);
  }

  getOrganizationConfig(organizationId: string) {
    this.myConfigOrganization = this.myConfigService.getMyConfigOrganizationById(organizationId);
    this.selectedId = this.myConfigOrganization.organizationId;
  }

  goToAdminStart() {
    if ((this.myConfigService.hasOrganizationIdRole(this.organizationId, 'ParkingOperator', this.organizationId)) || (this.myConfigService.hasOrganizationIdRole(this.organizationId, 'ParkingAdministrator', this.organizationId))) {
      this.router.navigate(['/admin/admin-start']);
    }
  }

  showParkiflyHeader() {
    return this.router.url.includes("/parkifly-new");
  }

  login() {
    this.dialog.open(LoginComponent, {
      width: '500px',
      disableClose: false,
      panelClass: 'login-dialog-panel',
      data: { forceMobileLayout: true }
    });
  }

  goToHandleBooking() {
    this.router.navigate(['/parkifly/handle-booking']);
  }

  updateNavButtons() {
    const currentPath = this.location.path();
    this.showNavButtons = currentPath === '/parkifly/handle-booking' || currentPath === '/start';
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
